import { useEffect, useState } from "react";
import { fetchHseEventStatistics } from "../../../api/hse/events";
import { useTranslation } from "react-i18next";
import { ResourceLoading } from "../../utils/ResourceLoading";

export const HseEventStatistics = ({ onClose }) => {
  const { t } = useTranslation();
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        setLoading(true);
        const response = await fetchHseEventStatistics();
        setStatistics(response.data);
      } catch (error) {
        console.error("Error fetching HSE statistics:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStatistics();
  }, []);

  if (loading) {
    return <ResourceLoading size="base" />;
  }

  if (!statistics) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">{t("Kunne ikke laste statistikk")}</p>
      </div>
    );
  }

  const StatCard = ({ title, value, color = "bg-blue-500" }) => (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div
              className={`w-8 h-8 ${color} rounded-md flex items-center justify-center`}
            >
              <span className="text-white text-sm font-medium">{value}</span>
            </div>
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">
                {title}
              </dt>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );

  const ChartCard = ({ title, data, color = "bg-blue-500" }) => (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
        <div className="space-y-3">
          {Object.entries(data).map(([key, value]) => (
            <div key={key} className="flex items-center justify-between">
              <span className="text-sm text-gray-600">{key}</span>
              <span className="text-sm font-medium text-gray-900">{value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title={t("Totale hendelser")}
          value={statistics.total_events}
          color="bg-blue-500"
        />
        <StatCard
          title={t("Nylige hendelser")}
          value={statistics.recent_events}
          color="bg-green-500"
        />
        <StatCard
          title={t("Forfalte tiltak")}
          value={statistics.overdue_actions}
          color="bg-red-500"
        />
        <StatCard
          title={t("Kritiske hendelser")}
          value={statistics.events_by_severity?.critical || 0}
          color="bg-orange-500"
        />
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <ChartCard
          title={t("Hendelser etter type")}
          data={statistics.events_by_type}
        />
        <ChartCard
          title={t("Hendelser etter status")}
          data={statistics.events_by_status}
        />
      </div>

      <div className="flex justify-end">
        <button
          onClick={onClose}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {t("Lukk")}
        </button>
      </div>
    </div>
  );
};
